import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { INDIAN_CITIES } from '../utils/indianCities';
import { FaHeadset, FaCogs } from 'react-icons/fa';
import './Reviews.css';
import Seo from '../components/Seo';

const PAGE_SIZE_DEFAULT = Number(process.env.REACT_APP_REVIEWS_PAGE_SIZE || 10);
const REVIEW_POST_ENDPOINT =
  process.env.REACT_APP_REVIEW_ENDPOINT ||
  (process.env.REACT_APP_HOST_SERVER
    ? `${process.env.REACT_APP_HOST_SERVER}/api/reviews`
    : '/api/reviews');
const REVIEW_LIST_ENDPOINT =
  process.env.REACT_APP_REVIEW_LIST_ENDPOINT ||
  (process.env.REACT_APP_HOST_SERVER
    ? `${process.env.REACT_APP_HOST_SERVER}/api/reviews`
    : '/api/reviews');

const Star = ({ filled, onClick, size = 22, readOnly = false, ariaLabel }) => {
  const color = filled ? '#fbbf24' : '#e5e7eb';
  return (
    <button
      type="button"
      onClick={readOnly ? undefined : onClick}
      aria-label={ariaLabel}
      style={{
        width: size,
        height: size,
        lineHeight: 0,
        padding: 0,
        margin: '0 2px',
        background: 'transparent',
        border: 'none',
        cursor: readOnly ? 'default' : 'pointer'
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
        <path d="M12 .587l3.668 7.568L24 9.75l-6 5.854 1.416 8.262L12 19.771l-7.416 4.095L6 15.604 0 9.75l8.332-1.595z" />
      </svg>
    </button>
  );
};

const Stars = ({ value = 0, onChange, size, readOnly = false, labelPrefix }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center' }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        filled={n <= value}
        onClick={() => onChange?.(n)}
        size={size}
        readOnly={readOnly}
        ariaLabel={`${labelPrefix || 'rating'} ${n}`}
      />
    ))}
  </div>
);

// Read-only star row with precise partial fill using SVG clipPath
const StarHalfable = ({ fill = 1, size = 18 }) => {
  const clamped = Math.max(0, Math.min(1, Number(fill) || 0));
  const clipId = React.useMemo(() => `starClip_${Math.random().toString(36).slice(2)}`, []);
  const path = 'M12 .587l3.668 7.568L24 9.75l-6 5.854 1.416 8.262L12 19.771l-7.416 4.095L6 15.604 0 9.75l8.332-1.595z';
  const vb = 24;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={vb * clamped} height={vb} />
        </clipPath>
      </defs>
      {/* Base gray star */}
      <path d={path} fill="#e5e7eb" />
      {/* Gold fill clipped to percentage */}
      <path d={path} fill="#fbbf24" clipPath={`url(#${clipId})`} />
    </svg>
  );
};

const StarRow = ({ value = 0, size = 18 }) => {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, v - i));
        return <StarHalfable key={i} fill={fill} size={size} />;
      })}
    </div>
  );
};

const PlaceholderAvatar = ({ name, size = 36 }) => {
  const initials = String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: 'linear-gradient(135deg,#c7d2fe,#93c5fd)',
        color: '#0f172a',
        fontWeight: 700
      }}
    >
      {initials || 'U'}
    </div>
  );
};

const Reviews = ({ user, notify }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [step, setStep] = useState(1);

  // Step 1 ratings
  const [productRating, setProductRating] = useState(0);
  const [customerRating, setCustomerRating] = useState(0);

  // Step 2 details
  const [city, setCity] = useState('');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cityFocused, setCityFocused] = useState(false);
  const [cityActiveIndex, setCityActiveIndex] = useState(0);

  // Reviews data
  const [allReviews, setAllReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all'); // 'all' | 'popular'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  useEffect(() => {
    try {
      const root = document.getElementById('root');
      if (root) Modal.setAppElement(root);
    } catch {}
  }, []);

  const handleWriteReview = () => {
    if (!user) {
      try {
        // Hint for app to return to Reviews after successful sign-in
        sessionStorage.setItem('postLoginRedirect', '/reviews');
        window.dispatchEvent(new Event('open-auth-modal'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {}
      return;
    }
    setIsFormOpen(true);
    setStep(1);
    setProductRating(0);
    setCustomerRating(0);
    setCity('');
    setTitle('');
    setDetails('');
    setConsent(false);
    setSubmitError('');
  };

  const canProceed = productRating > 0 && customerRating > 0;

  const filteredCities = useMemo(() => {
    const q = String(city || '').trim().toLowerCase();
    if (!q) return [];
    const startsWith = [];
    const contains = [];
    for (const c of INDIAN_CITIES) {
      const lc = c.toLowerCase();
      if (lc.startsWith(q)) {
        startsWith.push(c);
      } else if (lc.includes(q)) {
        contains.push(c);
      }
    }
    return [...startsWith, ...contains].slice(0, 8);
  }, [city]);

  const findCityCanonical = (name) => {
    const q = String(name || '').trim().toLowerCase();
    if (!q) return '';
    for (const c of INDIAN_CITIES) {
      if (c.toLowerCase() === q) return c;
    }
    return '';
  };

  const overallStats = useMemo(() => {
    if (!allReviews.length) return { average: 0, count: 0 };
    const sum = allReviews.reduce((acc, r) => acc + Number(r.Rating || r.rating || 0), 0);
    const avg = sum / allReviews.length;
    return { average: avg, count: allReviews.length };
  }, [allReviews]);

  const popularSorted = useMemo(() => {
    const copy = [...allReviews];
    copy.sort((a, b) => Number(b.Rating || b.rating || 0) - Number(a.Rating || a.rating || 0));
    return copy;
  }, [allReviews]);

  const newestSorted = useMemo(() => {
    const copy = [...allReviews];
    copy.sort((a, b) => {
      const da = new Date(a['Review Date'] || a.reviewDate || a.createdAt || 0).getTime();
      const db = new Date(b['Review Date'] || b.reviewDate || b.createdAt || 0).getTime();
      return db - da;
    });
    return copy;
  }, [allReviews]);

  const activeList = tab === 'popular' ? popularSorted : newestSorted;
  const totalPages = Math.max(1, Math.ceil(activeList.length / pageSize));
  const currentItems = activeList.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [tab, pageSize]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(REVIEW_LIST_ENDPOINT, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        if (!r.ok) throw new Error('Failed to load reviews');
        const data = await r.json();
        const items =
          Array.isArray(data) ? data :
          Array.isArray(data?.reviews) ? data.reviews :
          Array.isArray(data?.items) ? data.items :
          Array.isArray(data?.data) ? data.data :
          Array.isArray(data?.payload) ? data.payload : [];
        if (isMounted) setAllReviews(items);
      } catch (e) {
        if (isMounted) setAllReviews([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Aggregate sub-ratings if present in the dataset
  const subAverages = useMemo(() => {
    const getAvg = (keys) => {
      let sum = 0;
      let count = 0;
      for (const r of allReviews) {
        for (const k of keys) {
          const val = Number(r[k]);
          if (!Number.isNaN(val) && val > 0) {
            sum += val;
            count += 1;
            break;
          }
        }
      }
      return count ? sum / count : 0;
    };
    const productAvg = getAvg(['productServicesRating']);
    const customerAvg = getAvg(['customerServiceRating']);
    console.log('productAvg', productAvg);
    console.log('customerAvg', customerAvg);
    return { productAvg, customerAvg };
  }, [allReviews]);

  const validateStep2 = () => {
    const errors = [];
    const canonicalCity = findCityCanonical(city);
    if (!canonicalCity) {
      if (city) setCity('');
      errors.push('Please select a valid city from suggestions.');
    } else if (canonicalCity !== city) {
      setCity(canonicalCity);
    }
    if (!title.trim()) errors.push('Please add a title.');
    if (!details.trim()) errors.push('Please add detailed review.');
    if (!consent) errors.push('Please accept the confirmation checkbox.');
    if (errors.length) {
      setSubmitError(errors.join(' '));
      return false;
    }
    setSubmitError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setSubmitting(true);
    try {
      const notifyUser = (message, type = 'success') => {
        try {
          if (typeof notify === 'function') {
            notify(message, type);
          } else {
            alert(message);
          }
        } catch {}
      };
      const payload = {
        userId: user?.id || user?._id || user?.userId,
        productServicesRating: productRating,
        customerServiceRating: customerRating,
        rating: Math.round(((productRating + customerRating) / 2) * 10) / 10,
        city,
        title,
        details,
        consent: true
      };
      const token = localStorage.getItem('token');
      const res = await fetch(REVIEW_POST_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      let responseBody = null;
      try {
        responseBody = await res.json();
      } catch {}
      const isSuccess = responseBody.successful;
      const apiMessage = responseBody.message;
      if (!isSuccess) {
        setSubmitError(apiMessage);
        notifyUser(apiMessage, 'error');
        setIsFormOpen(false);
        return;
      }
      // optimistic UI: add review locally
      const now = new Date().toISOString();
      const newItem = {
        title,
        userName: user?.name || 'You',
        createdAt: now,
        rating: payload.rating,
        details
      };
      setAllReviews((prev) => [newItem, ...prev]);
      setIsFormOpen(false);
      notifyUser(apiMessage, 'success');
    } catch (e) {
      const fallback = 'Could not submit review. Please try again.';
      setSubmitError(fallback);
      try {
        if (typeof notify === 'function') notify(fallback, 'error');
      } catch {}
      setIsFormOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Customer Reviews"
        description="Read honest KeepMyAsset customer reviews. See how families across India use our nominee alert and asset protection platform."
        path="/reviews"
        keywords="keepmyasset reviews, nominee alert reviews, asset protection testimonials"
      />
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px 48px' }}>
      {/* Intro */}
      <h1 style={{ margin: '0 0 12px', color: '#0f172a' }}>Customer Reviews</h1>
      <p style={{ color: '#475569', marginTop: 8 }}>
      See how customers rate their experience with our services. Honest reviews that help you make informed and confident decisions.
      </p>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={handleWriteReview}
          style={{
            background: 'linear-gradient(90deg,#4338ca,#2563eb)',
            color: '#fff',
            border: 'none',
            padding: '10px 16px',
            borderRadius: 12,
            cursor: 'pointer',
            boxShadow: '0 10px 20px rgba(37,99,235,0.25)'
          }}
        >
          Write Review
        </button>
      </div>

      {/* Overall banner - responsive layout */}
      {allReviews.length > 0 && <div className="rating-banner">
        <div className="rating-summary">
          <div className="rating-left">
            <div className="avg">{overallStats.average ? overallStats.average.toFixed(1) : '—'}</div>
            <div className="meta">
              {overallStats.count.toLocaleString()} reviews &amp; {overallStats.count.toLocaleString()} ratings
            </div>
            <div className="avg-stars">
              <StarRow value={overallStats.average} size={18} />
            </div>
          </div>
          <div className="rating-right">
            <div className="divider" aria-hidden />
            <div className="items">
              {subAverages.customerAvg > 0 && (
                <div className="rating-item">
                  <div className="label">
                    <FaHeadset />
                    <span>Customer Service</span>
                  </div>
                  <div className="value">
                    <StarRow value={subAverages.customerAvg} size={16} />
                  </div>
                </div>
              )}
              {subAverages.productAvg > 0 && (
                <div className="rating-item">
                  <div className="label">
                    <FaCogs />
                    <span>Product Services</span>
                  </div>
                  <div className="value">
                    <StarRow value={subAverages.productAvg} size={16} />
                  </div>
                </div>
              )}
              {!subAverages.customerAvg && !subAverages.productAvg && (
                <div className="empty">Ratings breakdown will appear as users start sharing detailed reviews.</div>
              )}
            </div>
          </div>
        </div>
      </div>}

      {/* Tabs */}
      {allReviews.length > 0 && <div style={{ display: 'flex', gap: 10, marginTop: 24, borderBottom: '1px solid #e5e7eb' }}>
        <button
          type="button"
          onClick={() => setTab('all')}
          className="tab-btn"
          style={{
            padding: '8px 12px',
            border: 'none',
            background: 'transparent',
            borderBottom: tab === 'all' ? '3px solid #2563eb' : '3px solid transparent',
            color: tab === 'all' ? '#1e3a8a' : '#64748b',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          All Reviews
        </button>
        <button
          type="button"
          onClick={() => setTab('popular')}
          className="tab-btn"
          style={{
            padding: '8px 12px',
            border: 'none',
            background: 'transparent',
            borderBottom: tab === 'popular' ? '3px solid #2563eb' : '3px solid transparent',
            color: tab === 'popular' ? '#1e3a8a' : '#64748b',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Popular Reviews
        </button>
        <div style={{ marginLeft: 'auto', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ padding: '6px 8px', borderRadius: 10, border: '1px solid #e5e7eb' }}
          >
            {[5, 10, 20].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>}

      {/* List */}
      <div style={{ marginTop: 10 }}>
        {loading ? (
          <div style={{ color: '#64748b' }}>Loading reviews…</div>
        ) : currentItems.length === 0 ? (
          <div style={{ color: '#475569', background: '#f1f6f5', borderRadius: 14, padding: '24px', lineHeight: 1.6 }}><strong style={{ color: '#172033' }}>Reviews are on their way.</strong><br />We publish reviews from customers who choose to share their experience. If you use KeepMyAsset, you can help future families make an informed decision.</div>
        ) : (
          currentItems.map((r, idx) => {
            const name = r.userName || 'Customer';
            const titleV = r.title || '';
            const ratingV = Number( r.rating || 0);
            const dateStr = r['Review Date'] ||  r.createdAt;
            const detailsV = r['Detailed Review'] || r.details || '';
            const dt = dateStr ? new Date(dateStr) : null;
            return (
              <div
                key={idx}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 14,
                  padding: '14px 16px',
                  marginTop: 12
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <PlaceholderAvatar name={name} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{name}</div>
                    {dt && <div style={{ color: '#94a3b8', fontSize: 12 }}>{dt.toLocaleDateString()}</div>}
                  </div>
                  <Stars value={Math.round(ratingV)} readOnly size={18} />
                </div>
                {titleV ? <div style={{ marginTop: 8, fontWeight: 600 }}>{titleV}</div> : null}
                {detailsV ? <div style={{ marginTop: 6, color: '#334155' }}>{detailsV}</div> : null}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: page === 1 ? '#f1f5f9' : '#fff',
              color: '#0f172a',
              cursor: page === 1 ? 'default' : 'pointer'
            }}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => {
            const n = i + 1;
            const active = n === page;
            return (
              <button
                type="button"
                key={n}
                onClick={() => setPage(n)}
                style={{
                  padding: '8px 10px',
                  borderRadius: 10,
                  border: active ? '1px solid #2563eb' : '1px solid #e5e7eb',
                  background: active ? 'linear-gradient(90deg,#4338ca,#2563eb)' : '#fff',
                  color: active ? '#fff' : '#0f172a',
                  cursor: 'pointer'
                }}
              >
                {n}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: page === totalPages ? '#f1f5f9' : '#fff',
              color: '#0f172a',
              cursor: page === totalPages ? 'default' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Review Form Modal */}
      <Modal
        isOpen={isFormOpen}
        onRequestClose={() => setIsFormOpen(false)}
        contentLabel="Write Review"
        style={{
          overlay: {
            background: 'rgba(2,6,23,0.6)',
            zIndex: 11000,
            backdropFilter: 'blur(2px)'
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: 'min(96vw, 640px)',
            background: '#fff',
            borderRadius: 16,
            padding: 0,
            border: '1px solid #e2e8f0',
            boxShadow: '0 24px 64px rgba(2,6,23,0.35)'
          }
        }}
      >
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, color: '#0f172a' }}>
              {step === 1 ? 'Rate Your Experience' : 'Tell us more'}
            </h2>
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              aria-label="Close"
              style={{
                border: '1px solid #cbd5e1',
                background: '#fff',
                width: 32,
                height: 32,
                borderRadius: '50%',
                cursor: 'pointer',
                color: '#475569'
              }}
            >
              ×
            </button>
          </div>

          {step === 1 ? (
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#334155', marginBottom: 6 }}>Product Services Rating</div>
                <Stars value={productRating} onChange={setProductRating} size={28} labelPrefix="product" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#334155', marginBottom: 6 }}>Customer Service Rating</div>
                <Stars value={customerRating} onChange={setCustomerRating} size={28} labelPrefix="customer" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  style={{
                    background: '#f8fafc',
                    color: '#0f172a',
                    border: '1px solid #e2e8f0',
                    padding: '10px 14px',
                    borderRadius: 12,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!canProceed}
                  style={{
                    background: canProceed ? 'linear-gradient(90deg,#4338ca,#2563eb)' : '#94a3b8',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 14px',
                    borderRadius: 12,
                    cursor: canProceed ? 'pointer' : 'not-allowed',
                    boxShadow: canProceed ? '0 12px 24px rgba(37,99,235,0.25)' : 'none'
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#334155' }}>City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setCityActiveIndex(0);
                    }}
                    onFocus={() => setCityFocused(true)}
                    onBlur={() =>
                      setTimeout(() => {
                        setCityFocused(false);
                        const canonical = findCityCanonical(city);
                        if (!canonical && city) {
                          setCity('');
                        } else if (canonical && canonical !== city) {
                          setCity(canonical);
                        }
                      }, 120)
                    }
                    onKeyDown={(e) => {
                      if (!filteredCities.length) return;
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setCityActiveIndex((i) => Math.min(filteredCities.length - 1, i + 1));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setCityActiveIndex((i) => Math.max(0, i - 1));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        const pick = filteredCities[cityActiveIndex];
                        if (pick) {
                          setCity(pick);
                          setCityFocused(false);
                        }
                      } else if (e.key === 'Escape') {
                        setCityFocused(false);
                      }
                    }}
                    placeholder="Start typing your city"
                    autoComplete="off"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid #e5e7eb' }}
                  />
                  {cityFocused && filteredCities.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        marginTop: 6,
                        maxHeight: 220,
                        overflowY: 'auto',
                        boxShadow: '0 12px 24px rgba(2,6,23,0.08)',
                        zIndex: 20
                      }}
                      role="listbox"
                      aria-label="City suggestions"
                    >
                      {filteredCities.map((c, idx) => {
                        const isActive = idx === cityActiveIndex;
                        return (
                          <div
                            key={c}
                            role="option"
                            aria-selected={isActive}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setCity(c);
                              setCityFocused(false);
                            }}
                            onMouseEnter={() => setCityActiveIndex(idx)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: isActive ? '#eef2ff' : '#fff',
                              color: '#0f172a',
                              borderBottom: '1px solid #f1f5f9'
                            }}
                          >
                            {c}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, color: '#334155' }}>Review Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Add a short title"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid #e5e7eb' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, color: '#334155' }}>Detailed Review</label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={5}
                    placeholder="Share your experience…"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    style={{
                      marginTop: 3,
                      width: 18,
                      height: 18,
                      appearance: 'auto',
                      WebkitAppearance: 'checkbox',
                      MozAppearance: 'auto',
                      accentColor: '#2563eb'
                    }}
                  />
                  <span>I confirm this review is genuine and not fake.</span>
                </label>
              </div>
              {submitError ? (
                <div style={{ color: '#ef4444', marginTop: 6 }}>{submitError}</div>
              ) : null}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    background: '#f8fafc',
                    color: '#0f172a',
                    border: '1px solid #e2e8f0',
                    padding: '10px 14px',
                    borderRadius: 12,
                    cursor: 'pointer'
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !consent}
                  style={{
                    background: submitting || !consent ? '#94a3b8' : 'linear-gradient(90deg,#4338ca,#2563eb)',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 14px',
                    borderRadius: 12,
                    cursor: submitting || !consent ? 'not-allowed' : 'pointer',
                    boxShadow: submitting || !consent ? 'none' : '0 12px 24px rgba(37,99,235,0.25)'
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit Review'}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
    </>
  );
};

export default Reviews;
